import gql from 'graphql-tag'
import BaseDataTable from '@/components/BaseDataTable'

const ORGANISATION_TRUSTED_CONTACTS = 'organisationTrustedContacts'
const TRUSTED_CONTACTS = 'trustedContacts'

export const TRUSTED_CONTACT_LIST_ROW_FRAGMENT = gql`
  fragment TrustedContactListRowFragment on TrustedContact {
    id
    status
    managedStatus
    statusUpdatedAt
    viewerCanApprove(account: $companyId)
    viewerCanUpdate(account: $companyId)
    viewerCanDelete(account: $companyId)
    viewerCanReInvite(account: $companyId)
    skills {
      name
    }
    company {
      name
    }
    links
    notes {
      paginatorInfo {
        total
      }
    }
    worker {
      id
      name
      initials
      jobTitle
      avatar
      avatarPath @client
      isCurrentlyHired(account: $companyId)
      viewerCanContact(account: $companyId)
      viewerCanOfferJob(account: $companyId)
      address {
        country {
          isoCode
          name
        }
        city
        state
      }
      market {
        code
        name
      }
      currency
      dayRate
      skills {
        name
      }
      rtwVerifiedAt
      rtwExpiresAt
      hires(companies: $allAccounts, isAccepted: true) {
        paginatorInfo {
          total
        }
      }
      activeHires: hires(companies: $allAccounts, isAccepted: true, status: ACTIVE) {
        paginatorInfo {
          total
        }
      }
      pendingHires: hires(companies: $allAccounts, isAccepted: false) {
        paginatorInfo {
          total
        }
      }
      primaryBusinessEntity {
        name
      }
      hiresWithAttribution {
        recruiter {
          name
        }
        recruiterFee
        recruiterManagesWorkers
        recruiterOwnershipStartDate
        recruiterOwnershipDays
        recruiterOwnershipDaysLeft
      }
    }
  }
`

const TrustedContactsQuery = (queryName) => gql`
  query trustedContactsList(
    $accounts: [ID!]
    $allAccounts: [ID!]
    $page: Int!
    $perPage: Int!
    $search: String
    $countries: [String!]
    $states: [String!]
    $orderBy: [TrustedContactOrderByClauseInput!]
    $invitedByUsers: InvitedByInput
    $skills: [ID!]
    $statuses: [ContactStatus!]
    $companyId: ID!
    $origin: TrustedContactOrigin
    $businessSetup: [BusinessEntityType!]
    $hireHistory: HireHistoryFilterInput
    $hireStatus: HireStatusFilterInput
    $staffingAgencyStatus: TrustedContactStaffingAgencyStatus
    $customFields: [CustomFieldTypeValueInput!]
  ) {
    trustedContacts: ${queryName}(
      accounts: $accounts
      page: $page
      first: $perPage
      search: $search
      countries: $countries
      states: $states
      orderBy: $orderBy
      invitedByUsers: $invitedByUsers
      skills: $skills
      statuses: $statuses
      origin: $origin
      businessSetup: $businessSetup
      hireHistory: $hireHistory
      hireStatus: $hireStatus
      staffingAgencyStatus: $staffingAgencyStatus
      customFields: $customFields
    ) {
      paginatorInfo {
        ...PaginatorInfoFragment
      }
      data {
        ...TrustedContactListRowFragment
      }
    }
  }
  ${BaseDataTable.fragments.pagination}
  ${TRUSTED_CONTACT_LIST_ROW_FRAGMENT}
`

export const OrganisationTrustedContactsList = TrustedContactsQuery(ORGANISATION_TRUSTED_CONTACTS)
export const TrustedContactsList = TrustedContactsQuery(TRUSTED_CONTACTS)

export const TRUSTED_CONTACT_FILTER_DATA_FRAGMENT = gql`
  fragment TrustedContactFilterDataFragment on TrustedContact {
    id
    invitedByUser {
      id
      name
    }
    company {
      id
      name
    }
    worker {
      id
      avatarPath @client
      address {
        country {
          isoCode
          name
        }
        state
      }
      primaryBusinessEntity {
        localisedName
        type
      }
      market {
        code
        name
      }
    }
  }
`

const TrustedContactsFilterDataQuery = (queryName) => gql`
  query trustedContacts($first: Int, $statuses: [ContactStatus!], $accounts: [ID!])  {
    trustedContacts: ${queryName}(first: $first, statuses: $statuses, accounts: $accounts) @connection(key: "trustedContactsFilters", filter:["accounts"]){
      data {
        ...TrustedContactFilterDataFragment
      }
    }
  }
  ${TRUSTED_CONTACT_FILTER_DATA_FRAGMENT}
`

export const TrustedContactsFilterData = TrustedContactsFilterDataQuery(TRUSTED_CONTACTS)
export const OrganisationTrustedContactsFilterData = TrustedContactsFilterDataQuery(
  ORGANISATION_TRUSTED_CONTACTS,
)

export const TRUSTED_CONTACT_SKILLS_FILTER_DATA_FRAGMENT = gql`
  fragment TrustedContactSkillsFilterDataFragment on TrustedContact {
    id
    skills {
      value: id
      label: name
    }
    worker {
      skills {
        value: id
        label: name
      }
    }
  }
`

const TrustedContactsSkillsFilterDataQuery = (queryName) => gql`
  query trustedContacts($first: Int, $page: Int, $statuses: [ContactStatus!], $accounts: [ID!]) {
    trustedContacts: ${queryName}(first: $first, page: $page, statuses: $statuses, accounts: $accounts) @connection(key: "trustedContactsSkills", filter:["accounts"]){
      paginatorInfo {
        hasMorePages
        currentPage
      }
      data {
      ...TrustedContactSkillsFilterDataFragment
      }
    }
  }
  ${TRUSTED_CONTACT_SKILLS_FILTER_DATA_FRAGMENT}
`

export const TrustedContactsSkillsFilterData =
  TrustedContactsSkillsFilterDataQuery(TRUSTED_CONTACTS)
export const OrganisationTrustedContactsSkillsFilterData = TrustedContactsSkillsFilterDataQuery(
  ORGANISATION_TRUSTED_CONTACTS,
)
